import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Orders_ListaResponse';
import '../contracts/Orders_LookupByQrcodeResponse';
import '../contracts/Orders_OrderItemStepsResponse';
import '../contracts/Orders_WorkOrderStepUpdateStatusInput';
import '../contracts/Orders_WorkOrderStepUpdateStatusResponse';
import '../contracts/Orders_WorkOrderStepsResponse';
import '../contracts/Orders_WorkOrdersResponse';
import '../contracts/Orders_RetrocederEtapaInput';
import '../contracts/Orders_RetrocederEtapaResponse';
/**
 * Composable for SO Production operations.
 * Manages work orders, production steps, QR code lookup,
 * and step status transitions for the production panel.
 */
export function useSOProducao() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/orders';

  const fetchWorkOrders = async (orderId: number) => {
    log('useSOProducao', 'fetchWorkOrders', 'Fetching work orders', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/work_orders/${orderId}`, { responseContract: 'Orders_WorkOrdersResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar ordens de trabalho');
    } catch (err) {
      logError('useSOProducao', 'fetchWorkOrders', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchWorkOrderSteps = async (workOrderId: number) => {
    log('useSOProducao', 'fetchWorkOrderSteps', 'Fetching steps', { workOrderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/work_order_steps/${workOrderId}`, { responseContract: 'Orders_WorkOrderStepsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar etapas');
    } catch (err) {
      logError('useSOProducao', 'fetchWorkOrderSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateStepStatus = async (stepId: number, status: string) => {
    log('useSOProducao', 'updateStepStatus', 'Updating step status', { stepId, status });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/work_order_step_update_status/${stepId}`, { status }, { inputContract: 'Orders_WorkOrderStepUpdateStatusInput', responseContract: 'Orders_WorkOrderStepUpdateStatusResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao atualizar status da etapa');
    } catch (err) {
      logError('useSOProducao', 'updateStepStatus', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const lookupByQrcode = async (qrcode: any) => {
    log('useSOProducao', 'lookupByQrcode', 'Looking up QR code', { qrcode });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lookup_by_qrcode/${encodeURIComponent(qrcode)}`, { responseContract: 'Orders_LookupByQrcodeResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'QR Code nao encontrado');
    } catch (err) {
      logError('useSOProducao', 'lookupByQrcode', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchOrders = async () => {
    log('useSOProducao', 'fetchOrders', 'Fetching all orders');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Orders_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar ordens');
    } catch (err) {
      logError('useSOProducao', 'fetchOrders', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Volta o item para a etapa anterior (chamado 2026-0027). Reabre a última
   * etapa concluída, aborta as posteriores em andamento; exige motivo (>= 5).
   */
  const retrocederEtapa = async (orderItemId: number, motivo: string) => {
    log('useSOProducao', 'retrocederEtapa', 'Rolling back one step', { orderItemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/retroceder_etapa/${orderItemId}`, { motivo }, { inputContract: 'Orders_RetrocederEtapaInput', responseContract: 'Orders_RetrocederEtapaResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao retroceder etapa');
    } catch (err) {
      logError('useSOProducao', 'retrocederEtapa', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchOrderItemSteps = async (orderItemId: number) => {
    log('useSOProducao', 'fetchOrderItemSteps', 'Fetching order item steps', { orderItemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/order_item_steps/${orderItemId}`, { responseContract: 'Orders_OrderItemStepsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar etapas do item');
    } catch (err) {
      logError('useSOProducao', 'fetchOrderItemSteps', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchWorkOrders,
    fetchWorkOrderSteps,
    fetchOrderItemSteps,
    retrocederEtapa,
    updateStepStatus,
    lookupByQrcode,
    fetchOrders
  };
}
